import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BookingDetails } from '../components/BookingDetails.tsx';
import { BookingDialog } from '../components/BookingDialog.tsx';
import { DayView, MonthView, WeekView, type SlotPick } from '../components/CalendarViews.tsx';
import { FindRoomDialog } from '../components/FindRoomDialog.tsx';
import { useAvailability, useRooms } from '../queries.ts';
import {
  addMinutes,
  addMonths,
  dayMinutes,
  fmtDate,
  roundQuarter,
  startOfMonth,
  startOfStockholmDay,
  startOfWeek,
} from '../time.ts';
import type { Busy, Me } from '../types.ts';

type View = 'day' | 'week' | 'month';

function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A remembered view is a convenience only.
  }
}

export function CalendarPage({ me }: { me: Me }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roomId: linkedRoom } = useParams();
  const rooms = useRooms();
  const [view, setView] = useState<View>(() =>
    readPref('view', ['day', 'week', 'month'] as const, 'day'),
  );
  const [day, setDay] = useState(() => startOfStockholmDay(new Date()));
  const [roomId, setRoomId] = useState<string>(() => readPref('room', [] as string[], ''));
  const [newBooking, setNewBooking] = useState<{
    roomId?: string;
    start?: Date;
    end?: Date;
    attendees?: number;
  } | null>(null);
  const [details, setDetails] = useState<Busy | null>(null);
  const [finding, setFinding] = useState(false);

  const bookable = useMemo(() => (rooms.data ?? []).filter((r) => r.canBook), [rooms.data]);
  const room = bookable.find((r) => r.id === roomId) ?? bookable[0];

  // A room QR code links to /room/:id: open that room's booking form.
  useEffect(() => {
    if (linkedRoom && bookable.some((r) => r.id === linkedRoom)) {
      setRoomId(linkedRoom);
      const start = roundQuarter(addMinutes(new Date(), 7));
      setNewBooking({ roomId: linkedRoom, start, end: addMinutes(start, 60) });
      navigate('/', { replace: true });
    }
  }, [linkedRoom, bookable, navigate]);

  const [from, to] = useMemo(() => {
    if (view === 'day') return [day, startOfStockholmDay(day, 1)];
    if (view === 'week') {
      const w = startOfWeek(day);
      return [w, startOfStockholmDay(w, 7)];
    }
    const g = startOfWeek(startOfMonth(day));
    return [g, startOfStockholmDay(g, 42)];
  }, [view, day]);
  const busy = useAvailability(from, to);

  const changeView = (v: View) => {
    setView(v);
    writePref('view', v);
  };
  const step = (dir: number) => {
    if (view === 'day') setDay(startOfStockholmDay(day, dir));
    else if (view === 'week') setDay(startOfStockholmDay(day, 7 * dir));
    else setDay(addMonths(day, dir));
  };
  const pick = (p: SlotPick) =>
    setNewBooking({ roomId: p.roomId, start: p.start, end: addMinutes(p.start, 60) });

  const rawTitle =
    view === 'day'
      ? fmtDate(day, { weekday: 'long', day: 'numeric', month: 'long' })
      : view === 'week'
        ? `${fmtDate(startOfWeek(day), { day: 'numeric', month: 'short' })} – ${fmtDate(startOfStockholmDay(startOfWeek(day), 6), { day: 'numeric', month: 'short', year: 'numeric' })}`
        : fmtDate(day, { month: 'long', year: 'numeric' });
  // Only the first letter: Swedish months and weekdays are otherwise lowercase.
  const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1);

  if (rooms.isLoading) return <p>{t('common.loading')}</p>;
  if (!bookable.length) return <p className="card">{t('cal.noRooms')}</p>;

  return (
    <>
      <div className="bar">
        <button className="btn alt sm" onClick={() => step(-1)} aria-label={t('cal.prev')}>
          ‹
        </button>
        <button className="btn alt sm" onClick={() => step(1)} aria-label={t('cal.next')}>
          ›
        </button>
        <button className="btn alt sm" onClick={() => setDay(startOfStockholmDay(new Date()))}>
          {t('cal.today')}
        </button>
        <h2 className="sp" style={{ margin: 0, fontSize: 18 }} aria-live="polite">
          {title}
        </h2>
      </div>
      <div className="bar">
        <div className="seg" role="group">
          {(['day', 'week', 'month'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => changeView(v)}>
              {t(`cal.${v}`)}
            </button>
          ))}
        </div>
        {view !== 'day' && room && (
          <select
            aria-label={t('cal.room')}
            style={{ width: 'auto', flex: 1 }}
            value={room.id}
            onChange={(e) => {
              setRoomId(e.target.value);
              writePref('room', e.target.value);
            }}
          >
            {bookable.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
        <button className="btn alt" onClick={() => setFinding(true)}>
          {t('cal.find')}
        </button>
        <button
          className="btn sp"
          onClick={() => {
            const start =
              view === 'day' && day > new Date()
                ? addMinutes(day, 9 * 60)
                : roundQuarter(addMinutes(new Date(), 15));
            setNewBooking({ roomId: room?.id, start, end: addMinutes(start, 60) });
          }}
        >
          + {t('cal.newBooking')}
        </button>
      </div>

      {view === 'day' && (
        <DayView
          day={day}
          rooms={bookable}
          busy={busy.data ?? []}
          onPick={pick}
          onOpen={setDetails}
        />
      )}
      {view === 'week' && room && (
        <WeekView
          weekStart={startOfWeek(day)}
          room={room}
          busy={busy.data ?? []}
          onPick={pick}
          onOpen={setDetails}
        />
      )}
      {view === 'month' && room && (
        <MonthView
          month={day}
          room={room}
          busy={busy.data ?? []}
          onDay={(d) => {
            setDay(d);
            changeView('day');
          }}
        />
      )}
      <p className="k">
        {t('cal.hint')} · {t('cal.tz')}
        {view === 'day' && dayMinutes(day) !== 1440 ? ` · ${t('cal.dst')}` : ''}
      </p>

      {newBooking && (
        <BookingDialog
          me={me}
          rooms={rooms.data ?? []}
          initial={newBooking}
          onClose={() => setNewBooking(null)}
        />
      )}
      {details?.bookingId && (
        <BookingDetails
          me={me}
          rooms={rooms.data ?? []}
          bookingId={details.bookingId}
          extra={{ person: details.person, company: details.company }}
          onClose={() => setDetails(null)}
        />
      )}
      {finding && (
        <FindRoomDialog
          day={day}
          onClose={() => setFinding(false)}
          onBook={(p) => {
            setFinding(false);
            setNewBooking(p);
          }}
        />
      )}
    </>
  );
}
